import * as React from "react";
import axios from "axios";
import { config } from "../../../config/config.jsx";
import CryptoJS from "crypto-js";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/solid";
import { Empty, Modal } from "antd";
import { EyeOutlined } from "@ant-design/icons";

const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

export default function PersonalisationHistory() {
  const encryptedUser = localStorage.getItem("user");
  const user = decryptData(encryptedUser);
  const userId = user?._id || user?.id;

  const apiLink = `${config.apiBaseUrl}${config.endpoints.personalisationHistory}${userId}`;

  const [loading, setLoading] = React.useState(false);
  const [apiData, setApiData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [sortConfig, setSortConfig] = React.useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);


  const fetchHistory = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await axios.get(apiLink, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      let personalisationData = response.data?.personalizationRes;
      if (personalisationData && !Array.isArray(personalisationData)) {
        personalisationData = [personalisationData];
      }
      setApiData(Array.isArray(personalisationData) ? personalisationData : []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = apiData.filter((transaction) => {
    const searchStr = searchTerm.toLowerCase();
    const transactionDate = new Date(transaction.createdAt);

    const passesDateFilter =
      (!startDate || transactionDate >= startDate) &&
      (!endDate || transactionDate <= endDate);

    const passesSearchFilter =
      transaction.verifyWith?.toLowerCase().includes(searchStr) ||
      transaction.trackingId?.toLowerCase().includes(searchStr) ||
      transaction.slipLayout?.toLowerCase().includes(searchStr) ||
      transaction.phone?.toLowerCase().includes(searchStr) ||
      transaction.status?.toLowerCase().includes(searchStr);

    return passesDateFilter && passesSearchFilter;
  });

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (sortConfig.key === "createdAt") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const TableHeader = ({ label, sortKey, className }) => {
    const isSorted = sortConfig.key === sortKey;
    const icon = isSorted ? (
      sortConfig.direction === "asc" ? (
        <ArrowUpIcon className="w-4 h-4 text-blue-600" />
      ) : (
        <ArrowDownIcon className="w-4 h-4 text-blue-600" />
      )
    ) : (
      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
    );

    return (
      <th
        scope="col"
        className={`${className} cursor-pointer hover:bg-gray-50`}
        onClick={() => sortKey && sortData(sortKey)}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{label}</span>
          <span className="ml-2">{icon}</span>
        </div>
      </th>
    );
  };

  const getStatusClass = (status) => {
    const lowerStatus = status?.toLowerCase();
    if (lowerStatus === "successfully") {
      return "bg-green-100 text-green-800";
    }
    if (lowerStatus === "pending") {
      return "bg-blue-100 text-blue-800";
    }
    if (lowerStatus === "rejected") {
      return "bg-red-100 text-red-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  const showModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalVisible(true);
  };

  const handleDownloadSlip = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `slip-${filename}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  React.useEffect(() => {
    fetchHistory();
  }, [userId]);

  const sortedTransactions = getSortedData(filteredTransactions);

  const paginatedData = sortedTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(sortedTransactions.length / pageSize);



  return (
    <div className="p-4 w-full max-w-full overflow-hidden">
      <h2 className="text-[clamp(1.2rem,2vw,2rem)] font-bold mb-4">
        Personalisation History
      </h2>



      {/* Results Count */}
      {!loading && sortedTransactions.length > 0 && (
        <div className="mb-4 text-sm text-gray-600">
          Showing {paginatedData.length} of {sortedTransactions.length} records
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!loading && sortedTransactions.length > 0 ? (
        <>
          <div className="relative overflow-hidden rounded-lg border border-gray-200 shadow">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <table className="w-full table-auto divide-y divide-gray-200 min-w-[600px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-[40px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        S/N
                      </th>
                      <TableHeader
                        label="Tracking id"
                        sortKey="trackingId"
                        className="w-[120px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                      />
                      <TableHeader
                        label="Status"
                        sortKey="status"
                        className="w-[100px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                      />
                      <th className="w-[clamp(120px,20vw,160px)] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                        Download slip
                      </th>
                      <TableHeader
                        label="Date"
                        sortKey="createdAt"
                        className="w-[clamp(80px,15vw,112px)] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                      />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((transaction, index) => (
                      <tr
                        key={transaction._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="w-[40px] px-2 py-2 whitespace-nowrap text-[clamp(0.8rem,1vw,0.75rem)] text-gray-900">
                          {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="w-[120px] px-2 py-2 whitespace-nowrap text-[clamp(0.8rem,1vw,0.75rem)] font-mono text-gray-900">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm font-bold tracking-widest capitalize text-gray-800">
                            {transaction.trackingId ? String(transaction.trackingId) : "N/A"}
                          </span>
                        </td>
                        <td className="w-[100px] py-2 whitespace-nowrap">
                          <span
                            className={`px-2 py-0.5 uppercase inline-flex text-xs leading-5 ml-2 rounded-full ${getStatusClass(
                              transaction.status
                            )}`}
                          >
                            {transaction.status}
                          </span>
                        </td>
                        <td className="w-[clamp(120px,20vw,160px)] px-2 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-4">
                            {transaction.slip ? (
                              <button
                                onClick={() =>
                                  handleDownloadSlip(
                                    transaction.slip,
                                    transaction._id || transaction.id
                                  )
                                }
                                className="text-blue-600 pointer-coarse hover:text-blue-800 font-medium text-[clamp(0.65rem,1vw,0.75rem)] flex items-center gap-1"
                              >
                                [Download]
                              </button>
                            ) : (
                              <span className="text-gray-400 text-[clamp(0.65rem,1vw,0.75rem)]">
                                No slip
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="w-[clamp(80px,15vw,112px)] px-2 py-2 whitespace-nowrap text-[clamp(0.8rem,1vw,0.75rem)] text-gray-600">
                          {format(
                            new Date(transaction.createdAt),
                            "dd/MM/yyyy"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span>Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>entries</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>

                <span className="text-sm px-3">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : !loading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span>No Personalisation history records found.</span>}
          />
        </div>
      ) : null}

      {/* Modal */}
      <Modal
        title="Personalisation Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        className="responsive-modal"
      >
        {selectedTransaction && (
          <div className="space-y-6 p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Slip Image */}
              <div className="flex flex-col">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Slip Image
                </h4>
                {selectedTransaction.slip ? (
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-50">
                    <img
                      src={selectedTransaction.slip}
                      alt="Personalisation Slip"
                      className="w-full h-auto max-h-64 object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg border">
                    <p className="text-gray-500 text-sm">
                      No slip image available
                    </p>
                  </div>
                )}
              </div>

              {/* Transaction Details */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Transaction Details
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Date & Time
                      </p>
                      <p className="text-sm text-gray-900 mt-1">
                        {format(
                          new Date(selectedTransaction.createdAt),
                          "PPpp"
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Status
                      </p>
                      <span
                        className={`mt-1 px-3 py-1 text-xs font-semibold rounded-full ${getStatusClass(
                          selectedTransaction.status
                        )}`}
                      >
                        {selectedTransaction.status}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Tracking ID
                    </p>
                    <p className="text-sm text-gray-900 font-mono mt-1 bg-gray-50 p-2 rounded">
                      {selectedTransaction.trackingId || "N/A"}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedTransaction.phone || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Verified With
                      </p>
                      <p className="text-sm text-gray-900 mt-1">
                        {selectedTransaction.verifyWith || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Slip Layout
                    </p>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedTransaction.slipLayout || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Reply Note
                    </p>
                    <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded min-h-[60px]">
                      {selectedTransaction.replyNote || "No notes available"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
